// ============================================================================
// Article Collection Types (Simplified - No Sub-Collections)
// ============================================================================

import type { SubscriptionTier } from './subscription';

// ============================================================================
// Core Types
// ============================================================================

/**
 * A collection is a flat container for items (articles and headings).
 * No hierarchical nesting of collections.
 */
export interface ArticleCollection {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  article_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * Item type - either an article reference or a heading
 */
export type CollectionItemType = 'article' | 'heading';

/**
 * A collection item can be either an article or a heading.
 * Headings can contain other items (nested via parent_id).
 */
export interface CollectionItem {
  id: string;
  collection_id: string;
  parent_id: string | null;
  order_index: number;
  item_type: CollectionItemType;
  created_at: string;
  updated_at: string;

  // For articles (item_type === 'article'):
  article_id?: string;

  // For headings (item_type === 'heading'):
  title?: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
}

/**
 * Collection item with joined article data (for display)
 */
export interface CollectionItemWithArticle extends CollectionItem {
  // Joined from articles table when item_type === 'article':
  article_title?: string;
  article_excerpt?: string | null;
  time_to_read_minutes?: number;
  is_public?: boolean;
}

/**
 * Tree node for rendering nested collection items
 */
export interface CollectionItemTreeNode extends CollectionItemWithArticle {
  children: CollectionItemTreeNode[];
  depth: number;
}

/**
 * Flattened item for prev/next navigation in reading mode
 */
export interface FlattenedArticleItem {
  article_id: string;
  title: string;
  collection_id: string;
  collection_name: string;
  globalIndex: number;
}

// ============================================================================
// Collection with contents
// ============================================================================

/**
 * Collection with all its items (for editing/viewing)
 */
export interface ArticleCollectionWithItems extends ArticleCollection {
  items: CollectionItemWithArticle[];
}

/**
 * Collection with items organized as a tree (for reading sidebar)
 */
export interface ArticleCollectionWithTree extends ArticleCollection {
  tree: CollectionItemTreeNode[];
  flattenedArticles: FlattenedArticleItem[];
}

// ============================================================================
// Request Types
// ============================================================================

export interface CreateArticleCollectionRequest {
  name: string;
  description?: string | null;
}

export interface UpdateArticleCollectionRequest {
  name?: string;
  description?: string | null;
}

export interface AddCollectionItemRequest {
  parent_id?: string | null;
  item_type: CollectionItemType;
  // For articles:
  article_id?: string;
  // For headings:
  title?: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  // Position (optional - defaults to end)
  order_index?: number;
}

export interface UpdateCollectionItemRequest {
  parent_id?: string | null;
  order_index?: number;
  // For headings only:
  title?: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
}

export interface ReorderCollectionItemsRequest {
  items: Array<{
    id: string;
    parent_id: string | null;
    order_index: number;
  }>;
}

export interface BulkAddArticlesRequest {
  article_ids: string[];
  parent_id?: string | null;
}

// ============================================================================
// Response Types
// ============================================================================

export interface CreateArticleCollectionResponse {
  collection: ArticleCollection;
}

export interface GetArticleCollectionResponse {
  collection: ArticleCollectionWithItems;
}

export interface GetCollectionTreeResponse {
  collection: ArticleCollectionWithTree;
}

export interface ListArticleCollectionsResponse {
  collections: ArticleCollection[];
}

export interface UpdateArticleCollectionResponse {
  collection: ArticleCollection;
}

export interface DeleteArticleCollectionResponse {
  success: boolean;
}

export interface AddCollectionItemResponse {
  item: CollectionItemWithArticle;
}

export interface UpdateCollectionItemResponse {
  item: CollectionItemWithArticle;
}

export interface DeleteCollectionItemResponse {
  success: boolean;
}

export interface ReorderCollectionItemsResponse {
  success: boolean;
}

export interface BulkAddArticlesResponse {
  added_count: number;
  items: CollectionItemWithArticle[];
}

// ============================================================================
// Tier Limits
// ============================================================================

export interface ArticleCollectionTierLimits {
  maxArticlesPerCollection: number | null;  // null = unlimited
  maxTotalCollections: number | null;
  maxHeadingDepth: number | null;           // null = unlimited nesting
}

export const ARTICLE_COLLECTION_TIER_LIMITS: Record<SubscriptionTier, ArticleCollectionTierLimits> = {
  foundation: {
    maxArticlesPerCollection: 20,
    maxTotalCollections: 5,
    maxHeadingDepth: 2,
  },
  deep_dive: {
    maxArticlesPerCollection: 100,
    maxTotalCollections: 25,
    maxHeadingDepth: 5,
  },
  mastermind: {
    maxArticlesPerCollection: null,
    maxTotalCollections: null,
    maxHeadingDepth: null,
  },
};

export function getCollectionTierLimits(tier: SubscriptionTier): ArticleCollectionTierLimits {
  return ARTICLE_COLLECTION_TIER_LIMITS[tier];
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Builds a tree structure from a flat array of items
 */
export function buildItemTree(items: CollectionItemWithArticle[]): CollectionItemTreeNode[] {
  const itemMap = new Map<string, CollectionItemTreeNode>();
  const rootNodes: CollectionItemTreeNode[] = [];

  // First pass: create all nodes
  for (const item of items) {
    itemMap.set(item.id, {
      ...item,
      children: [],
      depth: 0,
    });
  }

  // Second pass: build tree structure
  for (const item of items) {
    const node = itemMap.get(item.id)!;
    if (item.parent_id && itemMap.has(item.parent_id)) {
      const parent = itemMap.get(item.parent_id)!;
      node.depth = parent.depth + 1;
      parent.children.push(node);
    } else {
      rootNodes.push(node);
    }
  }

  // Sort children by order_index
  const sortChildren = (nodes: CollectionItemTreeNode[]) => {
    nodes.sort((a, b) => a.order_index - b.order_index);
    for (const node of nodes) {
      sortChildren(node.children);
    }
  };
  sortChildren(rootNodes);

  return rootNodes;
}

/**
 * Flattens a tree into a list of articles for navigation
 */
export function flattenArticles(
  tree: CollectionItemTreeNode[],
  collectionId: string,
  collectionName: string
): FlattenedArticleItem[] {
  const result: FlattenedArticleItem[] = [];

  const traverse = (nodes: CollectionItemTreeNode[]) => {
    for (const node of nodes) {
      if (node.item_type === 'article' && node.article_id) {
        result.push({
          article_id: node.article_id,
          title: node.article_title || 'Untitled',
          collection_id: collectionId,
          collection_name: collectionName,
          globalIndex: result.length,
        });
      }
      traverse(node.children);
    }
  };

  traverse(tree);
  return result;
}

/**
 * Finds the first article in a tree
 */
export function findFirstArticle(tree: CollectionItemTreeNode[]): CollectionItemTreeNode | null {
  for (const node of tree) {
    if (node.item_type === 'article') {
      return node;
    }
    const found = findFirstArticle(node.children);
    if (found) return found;
  }
  return null;
}

/**
 * Calculates the depth of an item in the tree
 */
export function getItemDepth(items: CollectionItem[], itemId: string): number {
  const itemMap = new Map(items.map(i => [i.id, i]));
  let depth = 0;
  let current = itemMap.get(itemId);

  while (current?.parent_id) {
    depth++;
    current = itemMap.get(current.parent_id);
  }

  return depth;
}
